import { useQuery } from '@tanstack/react-query';
import {
  Award,
  Bell,
  CheckCircle2,
  ChevronRight,
  Copy,
  LogOut,
  Receipt,
  ShieldCheck,
  Smartphone,
  Sparkles,
  User,
} from 'lucide-react';
import { type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { LanguageSwitcher } from '@/components/language-switcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * /perfil — home da conta do cliente.
 * Avatar com gradient + lista de atalhos com depth Apple-like.
 */
export function PerfilPage() {
  const { user, signOut } = useAuth();
  const { tenant } = useTenant();

  const cli = useQuery({
    queryKey: ['cliente-perfil', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_clientes')
        .select('nom_cliente, des_email, num_whatsapp, ind_whats_validado')
        .eq('cod_auth_user', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  async function sair() {
    await signOut();
    toast.success('Até logo!');
  }

  const iniciais = (cli.data?.nom_cliente ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Header de página */}
      <div className="octa-page-header octa-fade-in-up">
        <p className="text-overline text-muted-foreground uppercase">Minha conta</p>
        <h1>Perfil</h1>
      </div>

      {/* Hero card — avatar gigante com ring conic */}
      <Card variant="elevated" className="octa-scale-in overflow-hidden">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="relative">
            <div
              aria-hidden
              className="bg-octa-gradient absolute inset-0 -z-10 rounded-full opacity-30 blur-xl"
            />
            <div className="bg-octa-gradient text-primary-foreground shadow-octa-glow ring-background flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold ring-4">
              {iniciais === '?' ? <User className="h-7 w-7" /> : iniciais}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-extrabold tracking-tight">
              {cli.data?.nom_cliente ?? '...'}
            </p>
            <p className="text-muted-foreground truncate text-xs">
              {cli.data?.des_email ?? user?.email}
            </p>
            {cli.data?.num_whatsapp && (
              <p className="mt-1 flex items-center gap-1 text-[11px]">
                <span className="text-muted-foreground">WhatsApp: {cli.data.num_whatsapp}</span>
                {cli.data.ind_whats_validado && (
                  <span className="inline-flex items-center gap-0.5 font-semibold text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" /> validado
                  </span>
                )}
              </p>
            )}
          </div>
          <Button asChild size="sm" variant="outline" className="rounded-xl">
            <Link to="/perfil/editar">Editar</Link>
          </Button>
        </CardContent>
      </Card>

      {/* Lista de atalhos — cards com ícone colorido + chevron */}
      <div>
        <h2 className="octa-section-title">Sua conta</h2>
        <Card variant="elevated" className="overflow-hidden p-0">
          <div className="divide-border/40 divide-y">
            <ProfileRow
              to="/assinatura"
              icon={Sparkles}
              cor="from-sky-500 to-blue-700"
              titulo="Minha assinatura"
              sub="Plano, mensalidade e veículos"
            />
            <ProfileRow
              to="/lavagens"
              icon={Smartphone}
              cor="from-emerald-500 to-teal-600"
              titulo="Histórico de lavagens"
              sub="Veja tudo que você já lavou"
            />
            <ProfileRow
              to="/fidelidade"
              icon={Award}
              cor="from-rose-400 to-orange-500"
              titulo="Programa de fidelidade"
              sub="Saldo de pontos e cupons"
            />
            <ProfileRow
              to="/pagamentos"
              icon={Receipt}
              cor="from-violet-500 to-purple-700"
              titulo="Pagamentos"
              sub="Histórico de cobranças"
            />
          </div>
        </Card>
      </div>

      <div>
        <h2 className="octa-section-title">Preferências</h2>
        <Card variant="elevated" className="overflow-hidden p-0">
          <div className="divide-border/40 divide-y">
            <ProfileRow
              to="/perfil/dispositivos"
              icon={Bell}
              cor="from-rose-500 to-pink-600"
              titulo="Meus dispositivos"
              sub="Aparelhos que recebem push"
            />
            <ProfileRow
              to="/lgpd"
              icon={ShieldCheck}
              cor="from-slate-500 to-slate-700"
              titulo="Meus dados (LGPD)"
              sub="Exportar, anonimizar, consentimentos"
            />
          </div>
        </Card>
      </div>

      {tenant?.des_email_suporte && (
        <div>
          <h2 className="octa-section-title">Suporte</h2>
          <Card variant="elevated">
            <CardContent className="space-y-3 p-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">E-mail</p>
                <a
                  href={`mailto:${tenant.des_email_suporte}`}
                  className="text-primary font-semibold hover:underline"
                >
                  {tenant.des_email_suporte}
                </a>
              </div>
              {tenant.num_whatsapp_suporte && (
                <>
                  <div className="octa-divider" />
                  <div>
                    <p className="text-muted-foreground text-xs">WhatsApp</p>
                    <a
                      href={`https://wa.me/${tenant.num_whatsapp_suporte.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary font-semibold hover:underline"
                    >
                      {tenant.num_whatsapp_suporte}
                    </a>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <LanguageSwitcher />

      {/* Diagnóstico (1 clique pra colar em ticket) */}
      <DiagnosticosCard tenantCode={tenant?.cod_tenant ?? '-'} email={cli.data?.des_email ?? '-'} />

      <Button onClick={sair} variant="outline" size="lg" className="w-full rounded-2xl">
        <LogOut className="mr-2 h-4 w-4" /> Sair da conta
      </Button>
    </div>
  );
}

function ProfileRow({
  to,
  icon: Icon,
  cor,
  titulo,
  sub,
}: {
  to: string;
  icon: ComponentType<{ className?: string }>;
  cor: string;
  titulo: string;
  sub: string;
}) {
  return (
    <Link
      to={to}
      className="octa-tap hover:bg-accent/40 group flex items-center gap-3 p-4 transition-colors"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${cor} shadow-octa-sm text-white`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold leading-tight">{titulo}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{sub}</p>
      </div>
      <ChevronRight className="text-muted-foreground/60 group-hover:text-foreground h-4 w-4 shrink-0 transition-colors group-hover:translate-x-0.5" />
    </Link>
  );
}

/**
 * Card com diagnóstico pra cliente colar no e-mail/WhatsApp ao abrir
 * ticket de suporte. Útil pra distinguir bug nosso vs problema dele.
 */
function DiagnosticosCard({ tenantCode, email }: { tenantCode: string; email: string }) {
  const dados = [
    `Tenant: ${tenantCode}`,
    `E-mail: ${email}`,
    `URL: ${window.location.href}`,
    `Navegador: ${navigator.userAgent.slice(0, 100)}`,
    `Idioma: ${navigator.language}`,
    `Online: ${navigator.onLine ? 'sim' : 'não'}`,
    `Resolução: ${window.screen.width}x${window.screen.height}`,
    `Quando: ${new Date().toISOString()}`,
  ].join('\n');

  function copiar() {
    void navigator.clipboard.writeText(dados);
    toast.success('Dados copiados!', {
      description: 'Cole no e-mail ou WhatsApp do suporte.',
    });
  }

  return (
    <Card variant="flat" className="bg-muted/30">
      <CardContent className="space-y-2 p-4 text-sm">
        <p className="text-muted-foreground text-xs">
          Algum problema? Copie estes dados e envie pro suporte — ajuda a achar a causa rápido.
        </p>
        <pre className="bg-card/60 ring-border/40 max-h-32 overflow-auto rounded-xl p-3 text-[11px] leading-relaxed ring-1">
          {dados}
        </pre>
        <Button variant="outline" size="sm" onClick={copiar} className="w-full rounded-xl">
          <Copy className="mr-2 h-3.5 w-3.5" />
          Copiar dados de diagnóstico
        </Button>
      </CardContent>
    </Card>
  );
}
